import StatusChip from 'components/@extended/StatusChip';
import { Info } from 'components/icons';
import { Checkbox } from 'components/ui/checkbox';
import { Switch } from 'components/ui/switch';
import { Tooltip } from 'components/ui/tooltip';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetRequiredFieldMandatoryMutation } from 'store/api/back-office-settings';
import type { ContactKind, RequiredFieldElement, RequiredFieldForm, Setting } from 'types/back-office-settings';
import { cn } from 'utils/cn';
import { t, translations } from 'utils/getTranslations';
import { CONFIGURATION_CARD_CLASS, SETTING_GROUP_BAND_CLASS } from '../configurationCard';
import { MandatoryForPopover } from './MandatoryForPopover';
import {
    CARD_KEYS,
    CLEAR_STICKY_CLASS,
    CONTACT_KINDS,
    type RequiredFieldRow,
    requiredFieldRows,
    rowLabel,
} from './requiredFieldLayout';
import { CHIP_CLASS } from './SettingRow';
import { useSettingSave } from './useSettingSave';
import { useAdministratorField } from 'hooks/useAdministratorField';

const COPY = 'back-office-settings.required-fields';

const BAND_CLASS = 'bg-grey-50 text-fg new:bg-surface-subtle new:text-grey-600 px-3 py-2 text-xs font-semibold';
const CELL_CLASS = 'border-divider new:border-divider-soft border-t px-3 py-2.5 align-middle';
export const DIVIDER_CLASS = 'border-divider new:border-divider-soft';

const party = (kind: ContactKind) => (kind.startsWith('PERSON') ? 'person' : 'company');
const relationship = (kind: ContactKind) =>
    kind.endsWith('NON_CLIENT') ? 'non-client' : kind.endsWith('LEAD') ? 'lead' : 'client';

interface Props {
    form: RequiredFieldForm;
    entries: RequiredFieldElement[];
    /** Without UPDATE.BACK_OFFICE_SETTINGS: drawn dimmed, every control off. */
    readOnly: boolean;
}

/**
 * A form's Required fields card: the Client form's grid of fields by contact kind, or the Case and Timecard forms'
 * rows. A tick or a switch saves at once; the chip beside it says who the field is mandatory for.
 */
export function RequiredFieldsCard({ form, entries, readOnly }: Props) {
    const [setMandatory] = useSetRequiredFieldMandatoryMutation();
    const save = useSettingSave();
    const card = `${COPY}.cards.${CARD_KEYS[form]}`;
    const setTo = (entry: RequiredFieldElement, mandatory: boolean) =>
        void save(() => setMandatory({ form: entry.form, field: entry.field, kind: entry.kind, mandatory }).unwrap());
    const rows = requiredFieldRows(entries);
    const titleId = useId();
    const administratorFieldName = useAdministratorField().name;

    return (
        <div className={cn(CONFIGURATION_CARD_CLASS, 'new:rounded-[10px]', readOnly && 'opacity-60')}>
            <div id={titleId} className={SETTING_GROUP_BAND_CLASS}>
                {t(`${card}.title`)}
            </div>
            <p className={cn(DIVIDER_CLASS, 'text-grey-600 m-0 border-t px-5 py-3 text-[13px] leading-[19px]')}>
                {t(`${card}.description`)}
            </p>
            {form === 'CLIENT' ? (
                <ClientFormGrid
                    titleId={titleId}
                    rows={rows}
                    readOnly={readOnly}
                    setTo={setTo}
                    administratorFieldName={administratorFieldName}
                />
            ) : (
                rows.map((row) => (
                    <FieldRow
                        key={row.field}
                        form={form}
                        row={row}
                        readOnly={readOnly}
                        setTo={setTo}
                        administratorFieldName={administratorFieldName}
                    />
                ))
            )}
        </div>
    );
}

interface RowProps {
    readOnly: boolean;
    setTo: (entry: RequiredFieldElement, mandatory: boolean) => void;
}

/** The Client form: a row per field, a column per contact kind, the field column held while the grid scrolls. */
function ClientFormGrid({
    titleId,
    rows,
    readOnly,
    setTo,
    administratorFieldName,
}: RowProps & { titleId: string; rows: RequiredFieldRow[]; administratorFieldName: string }) {
    const intl = useIntl();
    const kindLabel = (kind: ContactKind) =>
        intl.formatList([t(`${COPY}.groups.${party(kind)}`), t(`${COPY}.columns.${relationship(kind)}`)], {
            type: 'unit',
        });

    return (
        <div
            role="group"
            aria-labelledby={titleId}
            className={cn(DIVIDER_CLASS, 'overflow-x-auto overscroll-x-contain border-t')}
        >
            <table aria-labelledby={titleId} className="w-full border-separate border-spacing-0 text-[13px]">
                <thead>
                    <tr>
                        <td
                            rowSpan={2}
                            className={cn(
                                BAND_CLASS,
                                'bg-paper sticky start-0 z-1 w-[260px] ps-5 max-sm:w-[156px] max-sm:min-w-[156px] max-sm:ps-4'
                            )}
                        />
                        {(['person', 'company'] as const).map((group) => (
                            <th
                                key={group}
                                scope="colgroup"
                                colSpan={3}
                                className={cn(BAND_CLASS, DIVIDER_CLASS, 'border-s text-start')}
                            >
                                <span className="sticky start-[272px] inline-block max-sm:start-[168px]">
                                    {t(`${COPY}.groups.${group}`)}
                                </span>
                            </th>
                        ))}
                    </tr>
                    <tr>
                        {CONTACT_KINDS.map((kind, index) => (
                            <th
                                key={kind}
                                scope="col"
                                className={cn(
                                    BAND_CLASS,
                                    DIVIDER_CLASS,
                                    'w-[13%] min-w-[170px] pt-0 text-start font-medium',
                                    index % 3 === 0 && 'border-s'
                                )}
                            >
                                {t(`${COPY}.columns.${relationship(kind)}`)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const label = rowLabel('CLIENT', row.field, administratorFieldName);
                        return (
                            <tr key={row.field}>
                                <RowHeader row={row} label={label} readOnly={readOnly} setTo={setTo} />
                                {CONTACT_KINDS.map((kind, index) => {
                                    const cell = row.cells.find((candidate) => candidate.kind === kind);
                                    const edge = index % 3 === 0 && 'border-s';
                                    if (!cell) {
                                        const reason = t(`${COPY}.${notOnFormReason(row, kind)}`);
                                        return (
                                            <td
                                                key={kind}
                                                title={reason}
                                                className={cn(
                                                    CELL_CLASS,
                                                    edge,
                                                    'bg-grey-50 new:bg-surface-subtle text-grey-500'
                                                )}
                                            >
                                                <span role="img" aria-label={reason}>
                                                    —
                                                </span>
                                            </td>
                                        );
                                    }
                                    const kinds = kindLabel(kind);
                                    return (
                                        <td key={kind} className={cn(CELL_CLASS, edge)}>
                                            <span className="flex min-h-6 items-center gap-3">
                                                <Checkbox
                                                    aria-label={intl.formatList([label, kinds], { type: 'unit' })}
                                                    checked={cell.mandatory}
                                                    disabled={readOnly}
                                                    onCheckedChange={(value) => setTo(cell, value)}
                                                    className={CLEAR_STICKY_CLASS}
                                                />
                                                {cell.mandatory ? (
                                                    <MandatoryForPopover
                                                        entry={cell}
                                                        context={t(`${COPY}.context`, { field: label, kind: kinds })}
                                                        size="cell"
                                                        readOnly={readOnly}
                                                    />
                                                ) : null}
                                            </span>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/** A kind a field has no cell for: the form never shows it to that party, or shows it to person clients only. */
function notOnFormReason(row: RequiredFieldRow, kind: ContactKind) {
    const sameParty = row.cells.some((cell) => cell.kind && party(cell.kind) === party(kind));
    if (sameParty) return 'person-clients-only';
    return party(kind) === 'person' ? 'not-on-person-form' : 'not-on-company-form';
}

/** The field name, named by itself alone, with its info and setting marks and, on VAT number, the row tick. */
function RowHeader({ row, label, readOnly, setTo }: RowProps & { row: RequiredFieldRow; label: string }) {
    const intl = useIntl();
    const nameId = useId();
    const { tick } = row;
    return (
        <th
            scope="row"
            aria-labelledby={nameId}
            className={cn(CELL_CLASS, 'bg-paper sticky start-0 z-1 ps-5 text-start font-normal max-sm:ps-4')}
        >
            <span className="flex flex-col gap-1.5 pointer-coarse:gap-3">
                <FieldName nameId={nameId} form="CLIENT" row={row} label={label} />
                {tick ? (
                    <label className="text-grey-600 flex cursor-pointer items-start gap-2 text-xs leading-[17px]">
                        <Checkbox
                            aria-label={intl.formatList([t(`${COPY}.vat-optional-own-country`), label], {
                                type: 'unit',
                            })}
                            checked={tick.mandatory}
                            disabled={readOnly}
                            onCheckedChange={(value) => setTo(tick, value)}
                        />
                        <span>{t(`${COPY}.vat-optional-own-country`)}</span>
                    </label>
                ) : null}
            </span>
        </th>
    );
}

/** A Case or Timecard form row: the name and its marks, the chip while on, then the switch at the trailing edge. */
function FieldRow({
    form,
    row,
    readOnly,
    setTo,
    administratorFieldName,
}: RowProps & { form: RequiredFieldForm; row: RequiredFieldRow; administratorFieldName: string }) {
    const nameId = useId();
    const entry = row.cells[0];
    const label = rowLabel(form, row.field, administratorFieldName);
    if (!entry) return null;
    return (
        <div
            className={cn(
                DIVIDER_CLASS,
                'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-2 border-t px-5 py-3 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:px-4 max-sm:pointer-coarse:gap-y-4'
            )}
        >
            <span className="min-w-0">
                <FieldName nameId={nameId} form={form} row={row} label={label} />
            </span>
            {entry.mandatory ? (
                <span className="flex max-sm:col-start-1 max-sm:row-start-2">
                    <MandatoryForPopover
                        entry={entry}
                        context={t(`${COPY}.context`, { field: label, kind: 'none' })}
                        size="row"
                        readOnly={readOnly}
                    />
                </span>
            ) : null}
            <span className="col-start-3 flex min-h-7 items-center max-sm:col-start-2 max-sm:row-start-1">
                <Switch
                    aria-labelledby={nameId}
                    checked={entry.mandatory}
                    disabled={readOnly}
                    onCheckedChange={(value) => setTo(entry, value)}
                />
            </span>
        </div>
    );
}

function FieldName({
    nameId,
    form,
    row,
    label,
}: {
    nameId: string;
    form: RequiredFieldForm;
    row: RequiredFieldRow;
    label: string;
}) {
    const info = `${COPY}.info.${form}.${row.field}`;
    const setting = row.cells.find((cell) => cell.setting)?.setting;
    return (
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span id={nameId} className="text-fg text-sm leading-5 font-medium">
                {label}
            </span>
            {info in translations ? <InfoMark field={label} text={t(info)} /> : null}
            {setting ? <SettingChip setting={setting} /> : null}
        </span>
    );
}

/** The mark of a row the form shows only while its back-office setting is on: the setting's name. */
export function SettingChip({ setting }: { setting: Setting }) {
    return (
        <StatusChip
            size="compact"
            className={cn(CHIP_CLASS, 'h-auto min-h-5.5 whitespace-normal max-sm:wrap-anywhere')}
            label={t(`back-office-settings.${setting}.label`)}
        />
    );
}

/**
 * The info mark: a tooltip at once on hover or focus, and a click or tap toggles it, since touch has neither. Its words
 * are also the button's description, as the tooltip is in the page only while it shows.
 * A press pins it apart from the hover state: a tap's compatibility mouse events open it before the click lands, so
 * toggling one state would shut it at once.
 */
function InfoMark({ field, text }: { field: string; text: string }) {
    const [shown, setShown] = useState(false);
    const [pinned, setPinned] = useState(false);
    const tipId = useId();
    return (
        <>
            <Tooltip
                content={text}
                delay={0}
                open={shown || pinned}
                onOpenChange={(open, { reason }) => {
                    setShown(open);
                    if (!open && reason !== 'trigger-hover') setPinned(false);
                }}
                closeOnClick={false}
            >
                <button
                    type="button"
                    aria-label={t(`${COPY}.about`, { field })}
                    aria-describedby={tipId}
                    onClick={() => {
                        if (pinned) setShown(false);
                        setPinned(!pinned);
                    }}
                    className={cn(
                        'text-grey-500 hit-area relative inline-flex cursor-default rounded-full',
                        NEW_FOCUS_RING
                    )}
                >
                    <Info className="size-3.5" />
                </button>
            </Tooltip>
            <span id={tipId} hidden>
                {text}
            </span>
        </>
    );
}
